import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { indexRowChartDataAtom, indexRowChartPollingAtom } from '../../state';
import { CachedChartResponse, ListedInstrument, QuoteChartEntry } from '../../types';
import { isDefined, Loadable, LoadableState } from '../../utils';
import { loadCachedChart } from '../chart';

export type UseIndexRowChartDataCommonParams = {
  instrument: LoadableState<ListedInstrument>;
  getCachedIntraDayChart: (ric: string) => Promise<CachedChartResponse>;
  isEnabled?: boolean;
};

export type UseIndexRowChartDataCommonOutput = {
  entries: LoadableState<QuoteChartEntry[]>;
  startPolling: () => void;
  stopPolling: () => void;
};

const UninitializedEntries = Loadable.uninitialized;

export const useIndexRowChartDataCommon = ({
  instrument,
  getCachedIntraDayChart,
  isEnabled,
}: UseIndexRowChartDataCommonParams): UseIndexRowChartDataCommonOutput => {
  const pollingTick = useAtomValue(indexRowChartPollingAtom);
  const [chartData, setChartData] = useAtom(indexRowChartDataAtom);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const lastHandledRequestTimeRef = useRef<number>(0);
  const ric = Loadable.hasValue(instrument) ? instrument.ric : undefined;
  const entries = (isDefined(ric) ? chartData[ric] : UninitializedEntries) ?? UninitializedEntries;

  const setEntries = useCallback(
    (transform: (currentEntries: LoadableState<QuoteChartEntry[]>) => LoadableState<QuoteChartEntry[]>): void => {
      if (!isDefined(ric)) {
        return;
      }

      setChartData(chartData => {
        const currentEntries = chartData[ric] ?? UninitializedEntries;
        return {
          ...chartData,
          [ric]: transform(currentEntries),
        };
      });
    },
    [ric, setChartData],
  );

  const loadEntries = useCallback(async (): Promise<void> => {
    if (!isDefined(ric)) {
      return;
    }

    await loadCachedChart({
      ric,
      setter: setEntries,
      loader: getCachedIntraDayChart,
    });
  }, [getCachedIntraDayChart, ric, setEntries]);

  const stopPolling = useCallback((): void => {
    setIsPolling(false);

    setEntries(prevEntries => {
      if (prevEntries.hasError) {
        return prevEntries;
      }
      if (Loadable.hasValue(prevEntries)) {
        return Loadable.loaded(prevEntries);
      }
      return Loadable.uninitialized;
    });
  }, [setEntries]);

  const startPolling = useCallback((): void => {
    if (isPolling || !isDefined(ric)) {
      return;
    }

    setIsPolling(true);

    if (!isEnabled) {
      return;
    }

    lastHandledRequestTimeRef.current = pollingTick;
    void loadEntries();
  }, [pollingTick, isPolling, loadEntries, ric, isEnabled]);

  useEffect(() => {
    setIsPolling(false);
    lastHandledRequestTimeRef.current = 0;
  }, [ric, setEntries]);

  useEffect(() => {
    if (!isEnabled || !isPolling || !isDefined(ric)) {
      return;
    }
    // Load on a new polling tick, or once for the initial load when polling was requested while
    // disabled (e.g. the instrument loaded before AppState became 'active' on cold start).
    const isNewTick = lastHandledRequestTimeRef.current !== pollingTick;
    if (!isNewTick && !Loadable.isUninitialized(entries)) {
      return;
    }
    lastHandledRequestTimeRef.current = pollingTick;
    void loadEntries();
  }, [pollingTick, isPolling, loadEntries, ric, isEnabled, entries]);

  return useMemo<UseIndexRowChartDataCommonOutput>(
    () => ({
      entries,
      startPolling,
      stopPolling,
    }),
    [entries, startPolling, stopPolling],
  );
};
